/**
 * Phase 13 / Task 13.2a (migration 0233) — a dispatcher's hand-entered tank readings.
 *
 * GET / POST /v1/tanks/:id/readings and PATCH …/:readingId/void. Types mirror
 * packages/api/src/routes/hauler-catalog/tank-readings.ts (copied by hand, like every
 * web-dispatch hook). A write refreshes the Tanks tab, whose fill rate and last level use the
 * readings.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery, type useApiOpts } from '@lodex-pro/web-shared';
import { TANK_LEVELS_QUERY_KEY } from './use-tank-levels';

export interface TankReadingRow {
  readonly id: string;
  /**
   * Tank registry PR 1 (0245): null when the reading was given in inches and the tank has no bbl
   * per inch yet ("level unknown" until it gets a size).
   */
  readonly levelBbl: number | null;
  /** The inches given; null for a reading given in BBL, and on shared rows. */
  readonly levelInches: number | null;
  readonly readAt: string;
  readonly source: string;
  readonly enteredByName: string | null;
  readonly createdAt: string;
  /** Set when a dispatcher voided a wrong entry; it then counts for nothing. */
  readonly voidedAt: string | null;
  /** 13.2b — the operator's or another hauler's reading of the same tank: read-only. */
  readonly shared?: boolean;
}

/** Exactly one of levelBbl or levelInches (tank registry PR 1). */
export type AddTankReadingBody = (
  | { levelBbl: number; levelInches?: never }
  | { levelInches: number; levelBbl?: never }
) & {
  readAt?: string;
  /** One per entry form, so a double submit or a retry stores one reading. */
  clientKey: string;
};

export const tankReadingsKey = (tankId: string): readonly unknown[] => [
  'tanks',
  tankId,
  'readings',
];

export function useTankReadings(
  apiOpts: ReturnType<typeof useApiOpts>,
  tankId: string,
  enabled: boolean,
): ReturnType<typeof useApiQuery<{ readings: readonly TankReadingRow[] }>> {
  return useApiQuery<{ readings: readonly TankReadingRow[] }>(
    apiOpts,
    `/v1/tanks/${tankId}/readings`,
    { queryKey: tankReadingsKey(tankId), enabled },
  );
}

export function useAddTankReading(
  apiOpts: ReturnType<typeof useApiOpts>,
  tankId: string,
): ReturnType<typeof useApiMutation<AddTankReadingBody, { reading: TankReadingRow }>> {
  const qc = useQueryClient();
  return useApiMutation<AddTankReadingBody, { reading: TankReadingRow }>(
    apiOpts,
    `/v1/tanks/${tankId}/readings`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: tankReadingsKey(tankId) });
        void qc.invalidateQueries({ queryKey: TANK_LEVELS_QUERY_KEY });
      },
    },
  );
}

export function useVoidTankReading(
  apiOpts: ReturnType<typeof useApiOpts>,
  tankId: string,
  readingId: string,
): ReturnType<typeof useApiMutation<Record<string, never>, { reading: TankReadingRow }>> {
  const qc = useQueryClient();
  return useApiMutation<Record<string, never>, { reading: TankReadingRow }>(
    apiOpts,
    `/v1/tanks/${tankId}/readings/${readingId}/void`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: tankReadingsKey(tankId) });
        void qc.invalidateQueries({ queryKey: TANK_LEVELS_QUERY_KEY });
      },
    },
  );
}
